import {
  Alert,
  Autocomplete,
  Button,
  Center,
  ComboboxItem,
  Divider,
  Group,
  Modal,
  OptionsFilter,
  Stack,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useListState } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { logError } from "api/diagnostic.service";
import CustomPageNotification from "components/layouts/CustomPageNotification";
import DefaultButtonAdd from "pages/admin/components/DefaultButtonAdd";
import { SavingModal } from "pages/modals";
import React, {
  startTransition,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Virtuoso } from "react-virtuoso";
import { IDataEntryFormProps } from "types/interface.types";
import {
  AdminOrderEntries,
  AdminOrderEntry,
  DebtorSummary,
  OrderDataEntryForm,
} from "types/model.types";

import {
  IconAlertCircle,
  IconCheck,
  IconDeviceFloppy,
  IconPrinter,
  IconProgressCheck,
} from "@tabler/icons-react";

import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_FORM_INVALID,
  MSG_DEFAULT_FORM_SUBMIT_ERR,
  MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
  NAVIGATE_ORDER,
} from "common/constants";
import {
  formatNZD,
  formatPercent,
  getGstAmount,
  getGstRateAsDecimal,
  getLocalDateString,
  safeNumber,
} from "common/utils";
import { useProgressiveDebtorFetch } from "hooks/useProgressiveDebtorFetch";

import { Select, Text } from "@mantine/core";
import { safeApiCall } from "api/common.service";
import {
  changeOrderDate,
  getAdminOrderById,
  submitAdminOrder,
  updateAdminOrderPrintState,
} from "api/order.service";
import { getStaffUsers } from "api/user.service";
import { navigateTo } from "common/navigation";
import SafeHtml from "components/SafeHtml";
import { useProductStore } from "stores/useProductStore";
import { computeUnitPrice } from "utils/pricing";
import { OrderEntryRow, OrderEntryRowHandle } from "./AdminOrderEntryRow";
import { OrderEntryHeaderRow } from "./DataEntryHeaderDefinitions";

const AdminOrderEntryPage: React.FC<IDataEntryFormProps> = ({
  recordId,
  onSaveComplete,
}) => {
  // keyed by entry._uid, not index — indices shift when a row is removed, which
  // would otherwise cause React (and this ref map) to associate the wrong
  // instance/local-state with a row after a delete.
  const rowRefs = useRef<Record<string, OrderEntryRowHandle | null>>({});
  //const apiFetchRef = useRef<boolean>(false);
  const [printOrderStage, setPrintOrderStage] = useState<number>(0);
  const [systemNotes, setSystemNotes] = useState<string | null>(null);
  const [showSystemNotes, setShowSystemNotes] = useState<boolean>(false);
  const allProducts = useProductStore((s) => s.allProducts);
  const [triggerReload, setTriggerReload] = useState(new Date()); // used to trigger reload of product data after adding new product
  const [invoiceToInputValue, setInvoiceToInputValue] = useState("");
  const [deliverToInputValue, setDeliverToInputValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [salesPeople, setSalesPeople] = useState<
    { value: string; label: string }[]
  >([]);
  const [overDue, setOverdue] = useState<boolean>(false);
  const [debtorUnresolved, setDebtorUnresolved] = useState<boolean>(false);
  const [selectedDebtor, setSelectedDebtor] = useState<DebtorSummary | null>(
    null,
  );
  const [selectedDeliverToDebtor, setSelectedDeliverToDebtor] =
    useState<DebtorSummary | null>(null);
  const invoiceToRef = useRef<HTMLInputElement>(null);
  const deliverToRef = useRef<HTMLInputElement>(null);
  const [activeCustomer, setActiveCustomer] = useState<boolean>(true);
  const [entries, handlers] = useListState<AdminOrderEntry>([]);
  const [invoicePrinted, setInvoicePrinted] = useState(false);
  const [packingSlipPrinted, setPackingSlipPrinted] = useState(false);
  const {
    debtors,
    loading: debtorLoading,
    error: debtorError,
  } = useProgressiveDebtorFetch();
  const [fetchingInvoices, setFetchingInvoices] = useState(false);
  const [saving, setSaving] = React.useState(false);
  const [initialData, setInitialData] = useState<OrderDataEntryForm | null>(
    null,
  );
  const [orderDate, setOrderDate] = useState<string | null>(
    new Date().toUTCString(),
  );
  const debtorOptions: ComboboxItem[] = debtors
    .filter((d) => d.active === true)
    .map((debtor) => ({
      value: `${debtor.id}`,
      label: `${debtor.name} [${debtor.id}]`,
    }));
  const optionsFilter: OptionsFilter = ({ options, search }) => {
    const searchWords = search.toLowerCase().trim().split(" ");

    return (options as ComboboxItem[]).filter((option) => {
      const labelWords = option.label?.toLowerCase().trim().split(" ") ?? [];
      return searchWords.every((word) =>
        labelWords.some((labelWord) => labelWord.includes(word)),
      );
    });
  };
  function formatLabel(email: string): string {
    const namePart = email.split("@")[0];
    return namePart.replace(/\./g, " ");
  }

  const resolveDebtorLabel = (id: number): string => {
    const l = debtors.length;
    return debtorOptions.find((d) => Number(d.value) === id)?.label ?? "";
  };

  // Define our useForm and must be declared before any UseEffects
  const form = useForm<OrderDataEntryForm>({
    initialValues: initialData ?? {
      id: 0,
      summary: {
        id: 0,
        invoiceId: 0,
        createdOn: getLocalDateString(),
        orderStage: 0,
        invoiceToDebtorId: 0,
        invoiceToDebtorName: "",
        deliverToDebtorId: 0,
        deliverToDebtorName: "",
        yourOrderNo: "",
        notes: "",
        notes2: "",
        salesPerson: "",
        isPickingListPrinted: false,
        isPackingSlipPrinted: false,
        isInvoicePrinted: false,
        isProcessed: false,
        totalAmount: 0,
        totalGst: 0,
      },
      entries: [],
    },
  });
  const { summary } = form.getValues();

  // Pricing here depends on allProducts being current — the store otherwise
  // only refreshes on login or a ~5-minute interval, which is too wide a
  // window for a page where mispriced lines get saved. One extra delta fetch
  // per order-entry visit is cheap; per-line fetches would not be.
  useEffect(() => {
    useProductStore.getState().syncModifiedProducts();
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchUsers = async () => {
      const users = await safeApiCall(getStaffUsers());
      const mapped = users
        .map((u: { userName: string }) => ({
          value: u.userName,
          label: formatLabel(u.userName),
        }))
        .sort(
          (
            a: { value: string; label: string },
            b: { value: string; label: string },
          ) => a.label.localeCompare(b.label),
        ); // sort by label
      if (!mounted) return;

      setSalesPeople(mapped);
    };

    fetchUsers();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (debtors.length === 0 || !recordId) return;

    let mounted = true;
    const fetchInitialData = async () => {
      try {
        const response = await safeApiCall(getAdminOrderById(recordId));
        if (!mounted) return;

        if (response) {
          let notes = null;

          // invoiceTo and deliverTo are always the same
          if (response.summary.invoiceToDebtorId) {
            const label = resolveDebtorLabel(
              response.summary.invoiceToDebtorId,
            );
            // get debtor discount
            const debtor = debtors.find(
              (d) => d.id === response.summary.invoiceToDebtorId,
            );
            setSelectedDebtor(debtor ?? null);
            setSelectedDeliverToDebtor(debtor ?? null);

            if (!debtor) {
              // pricing will default to sell price level 1 below — surface this
              // rather than letting it happen silently
              setDebtorUnresolved(true);
              logError(
                new Error(
                  `AdminOrderEntryPage: debtor ${response.summary.invoiceToDebtorId} not found in debtor list for order ${recordId} — pricing will default to sell price level 1.`,
                ),
              );
            } else {
              setDebtorUnresolved(false);
            }

            if (debtor && debtor.active === false) {
              setActiveCustomer(false);
            }

            setOverdue(debtor?.overdue ?? false);
            notes = debtor?.specialNotes ?? null;

            setInvoiceToInputValue(label);
            setDeliverToInputValue(label);
          }

          setSystemNotes(notes);
          const created = response.summary.createdOn;

          if (created) {
            const iso = response.summary.createdOn; // "2026-05-27T00:00:00Z"
            const dateOnly = iso.split("T")[0]; // "2026-05-27"

            const [y, m, d] = dateOnly.split("-").map(Number);
            const localDate = new Date(); //y, m - 1, d);
            localDate.setFullYear(y, m - 1, d);
            localDate.setHours(0, 0, 0, 0);
            setOrderDate(localDate.toUTCString());
          } else {
            setOrderDate(new Date().toUTCString());
          }

          const updatedResponse = { ...response, entries: [] };
          // assign a stable client-only identity per entry — entries loaded
          // from the server always have a real DB id at this point
          const entriesWithUid = response.entries.map((e: AdminOrderEntry) => ({
            ...e,
            _uid: `id-${e.id}`,
          }));
          startTransition(() => {
            form.setValues(updatedResponse);
            handlers.setState(entriesWithUid);
          });
        }
      } catch (error) {
        logError(error);
      } finally {
        //
      }
    };
    fetchInitialData();

    return () => {
      mounted = false;
    };
  }, [recordId, debtors, triggerReload]);
  const invalid = entries.some((e) => e.supplied > e.ordered);
  const { total, gst } = useMemo(() => {
    const total = entries.reduce((sum, entry) => {
      const supplied = entry.supplied || 0;
      const unitPrice = entry.unitPrice || 0;
      const discount = entry.discount || 0;

      // line total = supplied × unitPrice − discount
      //const lineTotal = supplied * (unitPrice ? Number(unitPrice) : 0) * (1 - discount);
      const lineTotal = safeNumber(
        supplied * Number(unitPrice) * (1 - discount),
        0,
        2,
      );

      return sum + lineTotal;
    }, 0);

    const rounded = safeNumber(Math.round(total * 100) / 100, 0, 2);
    const gst = getGstAmount(rounded); // GST = 15% of total

    return { total: rounded, gst };
  }, [entries]);

  const handleRemoveEntry = (index: number) => {
    if (summary.isProcessed) return;

    const entry = entries[index];

    // If entry has no DB ID → remove immediately
    if (!entry.id) {
      if (entry._uid) delete rowRefs.current[entry._uid];
      handlers.remove(index);
      return;
    }

    // Toggle softDelete
    const newSoftDelete = !entry.softDelete;

    handlers.setItem(index, {
      ...entry,
      softDelete: newSoftDelete,
    });
  };

  // Pure computation — no state writes — so it can be used both for a single
  // row's live commit (handleUpdateEntry) and for building a fully
  // recalculated entries array synchronously on save (see handleSubmit),
  // where waiting on handlers.setItem's async state update isn't an option.
  const computeEntry = (
    updated: AdminOrderEntry,
    forceRecalc = false,
  ): AdminOrderEntry => {
    // Lookup product only if productId has changed
    const product = allProducts.items.find(
      (p) => Number(p.productCode) === updated.productId,
    );

    if (!product) {
      return { ...updated, description: "Unknown Product" };
    }

    const unitsCtn = product.unitsCtn || 1; // guard against 0 as well as null/undefined

    const raw = updated.unitPrice;
    // Normalize: treat empty string as null
    const updatedUnitPrice =
      raw === null || raw === undefined ? null : Number(raw);

    const needsRecalc =
      forceRecalc ||
      updatedUnitPrice === null ||
      Number.isNaN(updatedUnitPrice);

    // Authoritative price: same computeUnitPrice() used for the row's
    // instant local preview, so the two can never disagree.
    const newUnitPrice: number = needsRecalc
      ? computeUnitPrice(
          product,
          selectedDebtor?.sellPriceLevel,
          updated.productCode,
        )
      : safeNumber(updatedUnitPrice);

    const newCartonPrice =
      updated.productCode === "C" ? newUnitPrice : newUnitPrice * unitsCtn;

    const newEachPrice =
      updated.productCode === "E"
        ? newUnitPrice
        : safeNumber(newUnitPrice / unitsCtn);

    const rawTotal =
      updated.supplied * Number(newUnitPrice) * (1 - (updated.discount || 0));
    const total = safeNumber(rawTotal, 0, 2); // round per line
    const rawGst = total * getGstRateAsDecimal();
    const gstAmount = safeNumber(rawGst, 0, 2); // round per line

    let productTitle = product.title;
    if (productTitle.indexOf("CTN") < 0) {
      productTitle += ` (${product.unitsCtn} CTN)`;
    }

    return {
      ...updated,
      description: productTitle,
      unitPrice: newUnitPrice,
      eachPrice: newEachPrice,
      cartonPrice: newCartonPrice,
      discount: updated.discount,
      total: total,
      gst: gstAmount,
    };
  };

  const handleUpdateEntry = (
    index: number,
    updated: AdminOrderEntry,
    forceRecalc = false,
  ) => {
    if (summary.isProcessed) return; // should never happen as the remove buttons are disabled when processed, but just in case...
    handlers.setItem(index, computeEntry(updated, forceRecalc));
  };

  // mark as printed
  const handlePrintPickingList = async () => {
    if (invalid) {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: "One or more items have supplied greater than ordered.",
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      return;
    }

    try {
      await handleSubmit(false, false, true); // save first to ensure we have an order ID for the printout
      setPrintOrderStage(0);
      const { summary } = form.getValues();

      await safeApiCall(
        updateAdminOrderPrintState(
          Number(summary.id),
          true,
          summary.isPackingSlipPrinted,
          summary.isInvoicePrinted,
          summary.invoiceId,
        )
          .then((resp) => {
            if (form.getValues().summary.invoiceId === 0)
              form.setFieldValue("summary.invoiceId", resp.invoiceId);

            form.setFieldValue("summary.isPickingListPrinted", true);

            const id = recordId || summary.id;
            setTimeout(() => {
              navigateTo(`${NAVIGATE_ORDER}?id=${id}&s=${0}&e=1`);
            }, 500);
          })
          .catch((err) => {
            notifications.show({
              title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
              message: MSG_DEFAULT_FORM_SUBMIT_ERR,
              position: MantineNotificationPosition.TopCenter,
              color: MantineNotificationColor.Error,
            });
          }),
      );
    } catch (err) {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: MSG_DEFAULT_FORM_SUBMIT_ERR,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      // log but ignore
      logError(err);
    }
  };
  // mark as printed
  // packing slip can only be printed after picking list is printed
  const handlePrintPackingSlip = async () => {
    if (invalid) {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: "One or more items have supplied greater than ordered.",
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      return;
    }
    try {
      await handleSubmit(false, false, true); // save first to ensure we have an order ID for the printout
      setPrintOrderStage(1);
      const { summary } = form.getValues();
      const response = await safeApiCall(
        updateAdminOrderPrintState(
          Number(summary.id),
          true,
          true,
          summary.isInvoicePrinted,
          summary.invoiceId,
        ),
      );

      if (response && response.invoiceId) {
        if (form.getValues().summary.invoiceId === 0)
          form.setFieldValue("summary.invoiceId", response.invoiceId);
      }

      setPackingSlipPrinted(true);

      form.setFieldValue("summary.orderStage", 1);
      form.setFieldValue("summary.isPickingListPrinted", true);
      form.setFieldValue("summary.isPackingSlipPrinted", true);

      const id = recordId || summary.id;
      setTimeout(() => {
        navigateTo(`${NAVIGATE_ORDER}?id=${id}&s=${1}&e=1`);
      }, 500);
    } catch (err) {
      // log but ignore
      logError(err);
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: MSG_DEFAULT_FORM_SUBMIT_ERR,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
    }
  };

  const handlePrintInvoice = async () => {
    if (invalid) {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: "One or more items have supplied greater than ordered.",
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      return;
    }

    //  mark as printed
    // exception.. we need to set invoice has been printed
    await handleSubmit(false, false, true); // save first to ensure we have an order ID for the printout

    setPrintOrderStage(2);
    const { summary } = form.getValues();

    try {
      await safeApiCall(
        updateAdminOrderPrintState(
          Number(summary.id),
          summary.isPickingListPrinted,
          summary.isPackingSlipPrinted,
          true,
          summary.invoiceId,
        ),
      );
      setInvoicePrinted(true);

      form.setFieldValue("summary.isInvoicePrinted", true);
      const id = recordId || summary.id;
      setTimeout(() => {
        navigateTo(`${NAVIGATE_ORDER}?id=${id}&s=${2}&e=1`);
      }, 500);
    } catch (err) {
      logError(err);
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: MSG_DEFAULT_FORM_SUBMIT_ERR,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
    }
  };

  // determine if we can process this order
  const canProcess = (): boolean => {
    const { summary } = form.getValues();

    if (summary.isProcessed) {
      return false; // negate - already processed
    }

    // Otherwise, allow processing if not all slips are printed,
    // or if customer is inactive, or if saving
    const allPrinted =
      summary.isPackingSlipPrinted &&
      summary.isInvoicePrinted &&
      summary.isPickingListPrinted;
    return allPrinted && activeCustomer; // all printed and active customer
  };
  /**
   * Process the credit note.  This works by setting the isProcessed flag to true
   * and then calling handleSubmit to save the form.  The backend will handle the rest.
   * @returns
   */
  const handleProcess = async () => {
    // Let React commit state before submitting
    setTimeout(() => {
      handleSubmit(true, true);
    }, 0);
  };
  function stripId(name: string): string {
    if (!name) return "";
    // Matches " [digits]" at the end inside square brackets
    return name.replace(/\s*\[\d+\]$/, "").trim();
  }

  /**
   * Save the form data to the backend.
   * @param close If true, close the form after saving
   * @returns
   */
  const handleSubmit = async (
    close = false,
    process = false,
    silent = false,
  ) => {
    const formData: OrderDataEntryForm = form.getValues();
    if (formData.summary.isProcessed) return; // already processed...

    if (
      form.validate().hasErrors ||
      invoiceToInputValue === "" ||
      deliverToInputValue === "" ||
      formData.summary.salesPerson === "" ||
      (process && canProcess() === false)
    ) {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: MSG_DEFAULT_FORM_INVALID,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      return;
    }

    if (invoiceToInputValue !== deliverToInputValue) {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: "Invoice To and Deliver To doesn't match.",
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      return;
    }

    if (invalid) {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: "One or more items have supplied greater than ordered.",
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      return;
    }

    // form is invalid.
    setSaving(true);

    try {
      // Recompute totals/gst for every entry (supplied/discount may have
      // changed) but never force-recalculate a price that's already set —
      // computeEntry only derives a fresh unitPrice when one is missing
      // (forceRecalc=false here), so a manually-typed or previously-computed
      // price is retained on every subsequent save. Built synchronously since
      // handlers.setItem is async and the payload below can't wait on a
      // state update to land.
      const recalculatedEntries = entries.map((entry) => computeEntry(entry));

      // inject the input values into the payl oad
      const payload: OrderDataEntryForm = {
        ...formData,
        summary: {
          ...formData.summary,
          invoiceToDebtorName: stripId(invoiceToInputValue),
          deliverToDebtorName: stripId(deliverToInputValue),
          isProcessed: process,
        },
        entries: recalculatedEntries,
      };

      // reflect the recalculated values in the UI immediately, ahead of the
      // server round-trip
      handlers.setState(recalculatedEntries);

      const result = await safeApiCall(submitAdminOrder(payload));
      if (result?.error) {
        notifications.show({
          title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
          message: result?.error || "An unknown error occurred while saving.",
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Error,
        });
        return;
      }
      // Merge backend response into form
      if (!result?.entries || !Array.isArray(result.entries)) {
        notifications.show({
          title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
          message: "Invalid response: entries is missing or not an array.",
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Error,
        });

        return;
      }

      const updatedEntries: AdminOrderEntries = result.entries.map(
        (updatedEntry: AdminOrderEntry) => {
          const existing = entries?.find(
            (e) => e.productId === updatedEntry.productId,
          );

          return {
            ...existing,
            ...updatedEntry, // this includes the new `id`
            // preserve the row's client identity across the save round-trip;
            // fall back to an id-derived one for rows that weren't matched
            _uid: existing?._uid ?? `id-${updatedEntry.id}`,
          };
        },
      );
      // .sort(
      //   (a: AdminOrderEntry, b: AdminOrderEntry) => a.id - b.id,
      // );

      startTransition(() => {
        form.setValues({
          summary: {
            ...formData.summary,
            id: result.orderId,
          },
        });
        handlers.setState(updatedEntries);
      });

      if (!silent) {
        notifications.show({
          title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
          message: MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Success,
        });
      }

      (process || close) && onSaveComplete(close);
    } catch (error) {
      notifications.show({
        title: "Journal Entry",
        message: `Failed to submit journal. Please try again. `,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      logError(error);
    } finally {
      // done
      setSaving(false);
    }
  };
  const handleAddEntry = () => {
    if (summary.isProcessed) return; // should never happen as the add button is disabled when processed, but just in case...
    const newUid = crypto.randomUUID();
    handlers.append({
      id: 0,
      _uid: newUid,
      productId: 0,
      productCode: "C",
      description: "",
      ordered: 0,
      supplied: 0,
      discount: selectedDebtor ? selectedDebtor.discount : null,
      ref: "",
      isBackOrderItem: false,
      unitPrice: null,
      eachPrice: null,
      cartonPrice: null,
      total: 0,
      gst: 0,
      gstUsed: getGstRateAsDecimal(),
      priceLevel: selectedDebtor?.sellPriceLevel ?? 1,
    });

    // Wait for Virtuoso to mount the new row
    setTimeout(() => {
      rowRefs.current[newUid]?.focus();
    }, 0);
  };
  return (
    <Stack>
      <Title order={3}>
        Customer Order Entry
        {activeCustomer ? "" : " - Account Deactivated"}
      </Title>
      {Boolean(overDue) && (
        <CustomPageNotification title="Account Overdue!" message="" />
      )}
      {debtorUnresolved && (
        <CustomPageNotification
          title="Pricing Warning"
          message="Could not resolve this customer's price level — prices below may default to Level 1. Verify pricing before saving."
        />
      )}
      {!activeCustomer && (
        <div
          style={{
            background: "#ffe0e0",
            padding: "1rem",
            borderRadius: "4px",
          }}
        >
          <Center>
            <Text>{`The selected debtor "${selectedDebtor?.name}" is marked as inactive.  Orders are not permitted for inactive debtors.`}</Text>
          </Center>
        </div>
      )}
      <Group grow align="flex-start">
        <TextInput
          label="Order ID"
          styles={{
            input: {
              backgroundColor: "#f8f9fa", // light grey (light mode)
              color: "#1a1a1a", // dark readable text
              cursor: "default",
            },
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
          value={summary.id === 0 ? "New" : summary.id}
          readOnly
        />

        <DatePickerInput
          label="Order Date"
          value={orderDate}
          dropdownType="modal"
          readOnly={
            form.getValues().summary.isProcessed ||
            form.getValues().summary.id === 0
          } // only allow setting order date on new orders or unprocessed orders
          modalProps={{
            zIndex: 3000,
            size: "sm",
            withinPortal: true,
            trapFocus: false,
            centered: true,
            keepMounted: true,
          }}
          onChange={async (value: string | null) => {
            setOrderDate(value ?? null);
            if (value) {
              const dateOnly = value.split("T")[0]; // "2026-05-27"
              await changeOrderDate(Number(summary.id), dateOnly);
            }
          }}
          clearable
          w={250}
        />

        <Select
          label="Sales Person"
          placeholder="Select a sales person"
          clearable
          searchable
          data={salesPeople}
          {...form.getInputProps("summary.salesPerson")}
          comboboxProps={{
            withinPortal: true,
            zIndex: 3001,
          }}
        />
      </Group>
      <Group grow align="flex-start">
        <Autocomplete
          ref={invoiceToRef}
          clearable
          label="Invoice To (Debtor)"
          placeholder="Type name or ID"
          data={debtorOptions}
          filter={optionsFilter}
          value={invoiceToInputValue}
          comboboxProps={{
            withinPortal: true,
            zIndex: 3001,
          }}
          onFocus={() => {
            invoiceToRef.current?.select();
          }}
          onChange={setInvoiceToInputValue} // updates as user types
          onOptionSubmit={(selected) => {
            const originalLabel = invoiceToInputValue;
            const match = debtorOptions.find(
              (d) => d.label === selected || d.value === selected,
            );
            const id = match ? Number(match.value) : 0;
            form.setFieldValue("summary.invoiceToDebtorId", id);

            // get debtor
            const debtor = debtors.find((d) => d.id === id);
            form.setFieldValue("summary.salesPerson", debtor?.salesRep ?? "");
            setSelectedDebtor(debtor ?? null);
            setSystemNotes(debtor?.specialNotes ?? null);

            setActiveCustomer(debtor?.active ?? false);
            setOverdue(debtor?.overdue ?? false);

            setInvoiceToInputValue(match?.label ?? selected); // show full label in input

            // also default the deliver to to same as invoice to if not set
            //if (invoiceToInputValue !== deliverToInputValue) {
            form.setFieldValue("summary.deliverToDebtorId", id);
            setDeliverToInputValue(
              match?.label ?? selected /* use original input */,
            );
            //}
          }}
          readOnly={Number(summary.id) > 0}
          error={debtorError ?? undefined}
          style={{ zIndex: 3000 }}
          styles={{ label: { fontWeight: 600 } }}
        />

        <Autocomplete
          ref={deliverToRef}
          label="Deliver To (Debtor)"
          placeholder="Type name or ID"
          data={debtorOptions}
          clearable
          filter={optionsFilter}
          value={deliverToInputValue}
          comboboxProps={{
            withinPortal: true,
            zIndex: 3001,
          }}
          onFocus={() => {
            deliverToRef.current?.select();
          }}
          onChange={setDeliverToInputValue} // updates as user types
          onOptionSubmit={(selected) => {
            const match = debtorOptions.find(
              (d) => d.label === selected || d.value === selected,
            );
            const id = match ? Number(match.value) : 0;
            const debtor = debtors.find((d) => d.id === id);
            setSelectedDeliverToDebtor(debtor ?? null);
            setDeliverToInputValue(match?.label ?? selected); // show full label in input
          }}
          readOnly={Number(summary.id) > 0}
          error={debtorError ?? undefined}
          style={{ zIndex: 3000 }}
          styles={{ label: { fontWeight: 600 } }}
        />

        <TextInput
          label="Your Order #"
          placeholder="Enter customer order ref"
          styles={{
            label: { fontWeight: 600 },
          }}
          {...form.getInputProps("summary.yourOrderNo")}
        />
      </Group>
      <input type="hidden" {...form.getInputProps("summary.creditTo")} />
      <Group grow align="flex-start">
        <Textarea
          label="Notes 1"
          placeholder="Enter notes"
          styles={{
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
          {...form.getInputProps("summary.notes")}
          mt="md"
        />
        <Textarea
          label="Notes 2"
          placeholder="Enter notes"
          styles={{
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
          {...form.getInputProps("summary.notes2")}
          mt="md"
        />
      </Group>
      {/* running invoice total */}
      {form.getValues().summary.orderStage !== 0 && (
        <Group justify="flex-end" mt="md">
          <Stack gap={3}>
            <Group justify="flex-end">
              <Text size="md" fw={700}>
                Invoice Amount:
              </Text>
              <Text size="md" fw={700} c="dimmed">
                {formatNZD(total)}
              </Text>
            </Group>
            <Group justify="flex-end">
              <Text size="md" fw={700}>
                GST:
              </Text>
              <Text size="md" fw={700} c="dimmed">
                {formatNZD(gst)}
              </Text>
            </Group>
          </Stack>
        </Group>
      )}
      <Divider
        my="md"
        label={`Order Entries (Debtor Discounts: ${formatPercent(
          selectedDebtor?.discount,
          0,
          true,
        )})`}
        labelPosition="center"
      />
      <Title order={3}>Order Entries</Title>
      <Group justify="space-between" mt="md">
        <DefaultButtonAdd
          onClick={handleAddEntry}
          title={`${summary.id === 0 ? "You need to save order first to load back-orders" : ""} `}
          disabled={summary.id === 0 || summary.isProcessed}
          text=" Add New Item"
          variant="light"
        />
        <Group>
          <Button
            leftSection={
              <IconAlertCircle
                color={`${systemNotes ? "red" : "gray"}`}
                size={16}
              />
            }
            variant="outline"
            onClick={() => setShowSystemNotes(true)}
          >
            ATTENTION
          </Button>

          <Button
            onClick={() => handleSubmit()}
            title="Save journal. This does not process the journal."
            leftSection={<IconDeviceFloppy size={16} />}
            disabled={saving || !activeCustomer || summary.isProcessed}
          >
            Save
          </Button>
          <Button
            onClick={() => handleSubmit(true)}
            title="Save journal. This does not process the journal."
            leftSection={<IconDeviceFloppy size={16} />}
            disabled={saving || !activeCustomer || summary.isProcessed}
          >
            Save & Close
          </Button>
          {Number(summary.id) > 0 && (
            <Button
              onClick={handlePrintPickingList}
              disabled={saving || !activeCustomer}
              leftSection={<IconPrinter size={16} />}
              rightSection={
                <IconCheck
                  color={summary.isPickingListPrinted ? "white" : "gray"}
                  size={16}
                />
              }
            >
              Picking List
            </Button>
          )}
          {Number(summary.id) > 0 && summary.isPickingListPrinted && (
            <>
              <Button
                onClick={handlePrintPackingSlip}
                disabled={saving || !activeCustomer}
                leftSection={<IconPrinter size={16} />}
                rightSection={
                  <IconCheck
                    color={
                      summary.isPackingSlipPrinted ||
                      summary.isPackingSlipPrinted
                        ? "white"
                        : "gray"
                    }
                    size={16}
                  />
                }
              >
                Packing Slip
              </Button>
              <Button
                onClick={handlePrintInvoice}
                disabled={
                  saving ||
                  summary.isPackingSlipPrinted === false ||
                  !activeCustomer
                }
                leftSection={<IconPrinter size={16} />}
              >
                Invoice
              </Button>
              <Button
                title="Process the order. This action cannot be undone."
                onClick={handleProcess}
                disabled={
                  summary.id === 0 ||
                  !activeCustomer ||
                  entries.length === 0 ||
                  saving ||
                  summary.isProcessed ||
                  summary.isInvoicePrinted === false ||
                  summary.isPackingSlipPrinted === false
                }
                leftSection={<IconProgressCheck size={16} />}
              >
                Process
              </Button>
            </>
          )}
        </Group>
      </Group>
      {error && (
        <Alert title="Error" color="red">
          {error}
        </Alert>
      )}
      {entries.length > 0 && (
        <Stack gap="sm" className="form">
          <Virtuoso
            style={{ height: 650, overflowY: "auto", contain: "layout" }}
            totalCount={entries.length}
            increaseViewportBy={{ top: 200, bottom: 200 }}
            overscan={300}
            defaultItemHeight={48} // ← stabilizes measurement
            components={{
              Header: () => (
                <OrderEntryHeaderRow orderStage={summary.orderStage} />
              ),
            }}
            itemContent={(index) => {
              const entry = entries[index];
              // _uid should always be set (assigned on load/add) — the id-based
              // fallback here only guards against an entry that somehow slipped
              // through without one.
              const uid = entry._uid ?? `id-${entry.id}`;
              return (
                <OrderEntryRow
                  orderStage={form.getValues().summary.orderStage}
                  ref={(api) => {
                    rowRefs.current[uid] = api;
                  }}
                  key={`${uid}-${summary.orderStage}`}
                  entry={entry}
                  index={index}
                  isLastRow={index === entries.length - 1}
                  onNewEntry={handleAddEntry}
                  debtorPriceLevel={selectedDebtor?.sellPriceLevel}
                  onUpdate={(updated, forceCalc) =>
                    handleUpdateEntry(index, updated, forceCalc)
                  }
                  onRemove={() => handleRemoveEntry(index)}
                />
              );
            }}
          />
        </Stack>
      )}
      <SavingModal opened={saving} onClose={() => setSaving(false)} />
      <Modal
        opened={showSystemNotes}
        onClose={() => setShowSystemNotes(false)}
        closeOnEscape={true}
        title={"Attention Required"}
        size="xl"
        centered
        withinPortal
        zIndex={9000}
      >
        <SafeHtml html={systemNotes ?? ""} />
      </Modal>
    </Stack>
  );
};

export default AdminOrderEntryPage;
